import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listRichProducts,
  updateShopSettings,
  type ShopSettings,
  type RichProduct,
} from "@/lib/products";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { MediaManager } from "@/components/MediaManager";
import {
  Save,
  FolderOpen,
  Star,
  ShoppingBag,
  Image as ImageIcon,
  Palette,
  LayoutGrid,
  Sparkles,
  Tag,
  Ribbon,
  Smartphone,
  Monitor,
  Maximize2,
  PinIcon,
  MousePointerClick,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface Props {
  open: boolean;
  settings: ShopSettings | undefined;
  onClose: () => void;
  previewMode: "desktop" | "mobile";
  onPreviewModeChange: (m: "desktop" | "mobile") => void;
  liveEdit: boolean;
  onLiveEditChange: (v: boolean) => void;
}

function Group({
  title,
  helper,
  icon,
  children,
}: {
  title: string;
  helper?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
      <header className="space-y-0.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <span className="text-primary">{icon}</span>
          {title}
        </h3>
        {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function ShopEditor({
  open,
  settings,
  onClose,
  previewMode,
  onPreviewModeChange,
  liveEdit,
  onLiveEditChange,
}: Props) {
  const qc = useQueryClient();
  const { data: allProducts = [] } = useQuery<RichProduct[]>({
    queryKey: ["rich-products"],
    queryFn: listRichProducts,
    enabled: open,
  });

  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [heroCta, setHeroCta] = useState("");
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);
  const [featuredLayout, setFeaturedLayout] = useState<string>("grid");
  const [bgStyle, setBgStyle] = useState<string>("solid");
  const [bgColor, setBgColor] = useState<string>("");
  const [gradEnabled, setGradEnabled] = useState(false);
  const [gradFrom, setGradFrom] = useState<string>("");
  const [gradTo, setGradTo] = useState<string>("");
  const [cardStyle, setCardStyle] = useState<string>("standard");
  const [ribbonColor, setRibbonColor] = useState<string>("");
  const [ribbonGradFrom, setRibbonGradFrom] = useState<string>("");
  const [ribbonGradTo, setRibbonGradTo] = useState<string>("");
  const [ctaEnabled, setCtaEnabled] = useState(true);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [fullWidth, setFullWidth] = useState(false);
  const [stickyHeader, setStickyHeader] = useState(false);
  const [heroHeight, setHeroHeight] = useState<number>(0);
  const [heroBorderRadius, setHeroBorderRadius] = useState<number>(40);
  const [gapHeroFeatured, setGapHeroFeatured] = useState<number>(32);
  const [gapFeaturedProducts, setGapFeaturedProducts] = useState<number>(32);
  const [showMedia, setShowMedia] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setHeroTitle(settings.hero_title ?? "");
    setHeroSubtitle(settings.hero_subtitle ?? "");
    setHeroCta(settings.hero_cta_text ?? "");
    setHeroImage(settings.hero_image_url ?? null);
    setFeaturedIds(settings.featured_product_ids ?? []);
    setFeaturedLayout(settings.featured_layout_mode ?? "grid");
    setBgStyle(settings.background_style ?? "solid");
    setBgColor(settings.background_color ?? "");
    setGradEnabled(!!settings.background_gradient_enabled);
    setGradFrom(settings.background_gradient_from ?? "");
    setGradTo(settings.background_gradient_to ?? "");
    setCardStyle(settings.product_card_style ?? "standard");
    setRibbonColor(settings.ribbon_color ?? "");
    setRibbonGradFrom(settings.ribbon_gradient_from ?? "");
    setRibbonGradTo(settings.ribbon_gradient_to ?? "");
    setCtaEnabled(settings.hero_cta_enabled ?? true);
    setAiEnabled(settings.flightdeck_ai_enabled ?? true);
    setFullWidth(settings.hero_full_width ?? false);
    setStickyHeader(settings.hero_sticky_header ?? false);
    setHeroHeight(settings.hero_height_px ?? 0);
    setHeroBorderRadius(settings.hero_border_radius ?? 40);
    setGapHeroFeatured(settings.gap_hero_featured_px ?? 32);
    setGapFeaturedProducts(settings.gap_featured_products_px ?? 32);
  }, [settings, open]);

  const toggleFeatured = (id: string) =>
    setFeaturedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateShopSettings({
        hero_title: heroTitle,
        hero_subtitle: heroSubtitle,
        hero_cta_text: heroCta,
        hero_image_url: heroImage,
        featured_product_ids: featuredIds,
        featured_layout_mode: featuredLayout,
        background_style: bgStyle,
        background_color: bgColor || null,
        background_gradient_enabled: gradEnabled,
        background_gradient_from: gradFrom || null,
        background_gradient_to: gradTo || null,
        product_card_style: cardStyle,
        ribbon_color: ribbonColor || null,
        ribbon_gradient_from: ribbonGradFrom || null,
        ribbon_gradient_to: ribbonGradTo || null,
        hero_cta_enabled: ctaEnabled,
        flightdeck_ai_enabled: aiEnabled,
        hero_full_width: fullWidth,
        hero_sticky_header: stickyHeader,
        hero_height_px: heroHeight > 0 ? heroHeight : null,
        hero_border_radius: heroBorderRadius,
        gap_hero_featured_px: gapHeroFeatured,
        gap_featured_products_px: gapFeaturedProducts,
      });
      qc.invalidateQueries({ queryKey: ["shop-settings"] });
      toast.success("Shop updated");
      onClose();
    } catch (e) {
      toast.error((e as Error).message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {showMedia && (
        <MediaManager
          onClose={() => setShowMedia(false)}
          onSelect={(url) => {
            setHeroImage(url);
            setShowMedia(false);
          }}
        />
      )}
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
          <div className="sticky top-0 z-10 bg-background border-b border-border px-5 py-4 space-y-3">
            <SheetHeader className="space-y-1 text-left">
              <SheetTitle className="text-base">Edit storefront</SheetTitle>
              <SheetDescription className="text-xs">
                Each control below maps to a visible part of the customer shop.
              </SheetDescription>
            </SheetHeader>
            <div className="flex items-center justify-between gap-2">
              <div className="inline-flex rounded-full border border-border bg-muted p-0.5">
                <button
                  onClick={() => onPreviewModeChange("desktop")}
                  aria-label="Preview as desktop"
                  aria-pressed={previewMode === "desktop"}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                    previewMode === "desktop" ? "bg-card shadow-soft" : "text-muted-foreground",
                  )}
                >
                  <Monitor className="h-3 w-3" /> Desktop
                </button>
                <button
                  onClick={() => onPreviewModeChange("mobile")}
                  aria-label="Preview as mobile"
                  aria-pressed={previewMode === "mobile"}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                    previewMode === "mobile" ? "bg-card shadow-soft" : "text-muted-foreground",
                  )}
                >
                  <Smartphone className="h-3 w-3" /> Mobile
                </button>
              </div>
              <Button
                size="sm"
                className="rounded-full gap-1.5"
                onClick={handleSave}
                disabled={saving}
              >
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>

            {/* Live edit + CTA enable — prominent banner rows */}
            <div className="space-y-2">
              <div
                className={cn(
                  "flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors border",
                  liveEdit
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : "bg-muted/50 border-border",
                )}
              >
                <div className="min-w-0">
                  <p
                    className={cn(
                      "text-xs font-semibold flex items-center gap-1.5",
                      liveEdit ? "text-emerald-700 dark:text-emerald-400" : "text-foreground",
                    )}
                  >
                    <MousePointerClick className="h-3.5 w-3.5 shrink-0" />
                    Live edit {liveEdit ? "on" : "off"}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Click hero text to rename, drag to reorder, eye to hide.
                  </p>
                </div>
                <Switch checked={liveEdit} onCheckedChange={onLiveEditChange} />
              </div>

              <div
                className={cn(
                  "flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors border",
                  ctaEnabled
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : "bg-muted/50 border-border",
                )}
              >
                <div className="min-w-0">
                  <p
                    className={cn(
                      "text-xs font-semibold flex items-center gap-1.5",
                      ctaEnabled ? "text-emerald-700 dark:text-emerald-400" : "text-foreground",
                    )}
                  >
                    <Tag className="h-3.5 w-3.5 shrink-0" />
                    CTA button {ctaEnabled ? "visible" : "hidden"}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Show or hide the hero call-to-action button.
                  </p>
                </div>
                <Switch checked={ctaEnabled} onCheckedChange={setCtaEnabled} />
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <Group
              title="Hero layout"
              icon={<LayoutGrid className="h-4 w-4" />}
              helper="Controls how the hero behaves on the page."
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-xs font-medium flex items-center gap-1.5">
                    <Maximize2 className="h-3 w-3" /> Full-width hero (desktop)
                  </Label>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Hero breaks out of the page padding edge-to-edge.
                  </p>
                </div>
                <Switch checked={fullWidth} onCheckedChange={setFullWidth} />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-xs font-medium flex items-center gap-1.5">
                    <PinIcon className="h-3 w-3" /> Sticky header on scroll
                  </Label>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Compact title + CTA bar appears when the hero scrolls out.
                  </p>
                </div>
                <Switch checked={stickyHeader} onCheckedChange={setStickyHeader} />
              </div>
              <Field
                label={`Hero height: ${heroHeight > 0 ? `${heroHeight}px` : "auto"}`}
                hint="Override the hero height. 0 = auto."
              >
                <input
                  type="range"
                  min={0}
                  max={700}
                  step={20}
                  value={heroHeight}
                  onChange={(e) => setHeroHeight(Number(e.target.value))}
                  className="w-full"
                />
              </Field>
              <Field
                label={`Corner radius: ${heroBorderRadius}px`}
                hint="0 = sharp corners, 40 = default rounded."
              >
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={4}
                  value={heroBorderRadius}
                  onChange={(e) => setHeroBorderRadius(Number(e.target.value))}
                  className="w-full"
                />
              </Field>
              <Field
                label={`Gap: hero → featured: ${gapHeroFeatured}px`}
                hint="Space between the hero and featured strip."
              >
                <input
                  type="range"
                  min={0}
                  max={96}
                  step={8}
                  value={gapHeroFeatured}
                  onChange={(e) => setGapHeroFeatured(Number(e.target.value))}
                  className="w-full"
                />
              </Field>
              <Field
                label={`Gap: featured → products: ${gapFeaturedProducts}px`}
                hint="Space between featured strip and all products."
              >
                <input
                  type="range"
                  min={0}
                  max={96}
                  step={8}
                  value={gapFeaturedProducts}
                  onChange={(e) => setGapFeaturedProducts(Number(e.target.value))}
                  className="w-full"
                />
              </Field>
            </Group>

            <Group
              title="Hero banner"
              icon={<ImageIcon className="h-4 w-4" />}
              helper="The big banner at the top of the shop."
            >
              <Field label="Title">
                <Input
                  value={heroTitle}
                  onChange={(e) => setHeroTitle(e.target.value)}
                  className="rounded-xl"
                />
              </Field>
              <Field label="Subtitle">
                <Input
                  value={heroSubtitle}
                  onChange={(e) => setHeroSubtitle(e.target.value)}
                  className="rounded-xl"
                />
              </Field>
              <Field label="Primary CTA text" hint="The main button on the banner.">
                <Input
                  value={heroCta}
                  onChange={(e) => setHeroCta(e.target.value)}
                  className="rounded-xl"
                />
              </Field>
              <Field
                label="Hero background image"
                hint="Optional. Sits behind the banner with a dark overlay."
              >
                <div className="flex items-center gap-2">
                  {heroImage && (
                    <img
                      src={heroImage}
                      alt=""
                      className="h-12 w-16 rounded-lg object-cover border border-border"
                    />
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full gap-1.5"
                    onClick={() => setShowMedia(true)}
                  >
                    <FolderOpen className="h-3.5 w-3.5" /> {heroImage ? "Change" : "Choose"}
                  </Button>
                  {heroImage && (
                    <button
                      onClick={() => setHeroImage(null)}
                      className="text-xs text-muted-foreground underline"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </Field>
            </Group>

            <Group
              title="Featured products"
              icon={<Star className="h-4 w-4" />}
              helper="Pin selected products to the Featured strip above All products."
            >
              <Field label="Layout style" hint="How featured products are arranged.">
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { v: "grid", label: "Grid" },
                    { v: "bento", label: "Bento" },
                    { v: "carousel", label: "Carousel" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setFeaturedLayout(o.v)}
                      className={cn(
                        "rounded-xl border py-1.5 text-xs font-medium transition-all",
                        featuredLayout === o.v
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border hover:border-primary/40",
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Selected products" hint="Tap to toggle.">
                {allProducts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No products yet.</p>
                ) : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                    {allProducts.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => toggleFeatured(p.id)}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-xl border p-2 text-left text-xs transition-all",
                          featuredIds.includes(p.id)
                            ? "border-primary bg-primary/5 font-medium"
                            : "border-border hover:border-primary/40",
                        )}
                      >
                        {p.image_main_url ? (
                          <img
                            src={p.image_main_url}
                            alt=""
                            className="h-7 w-7 shrink-0 rounded-md object-cover"
                          />
                        ) : (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
                            <ShoppingBag className="h-3.5 w-3.5 text-muted-foreground/40" />
                          </div>
                        )}
                        <span className="flex-1 truncate">{p.name}</span>
                        {featuredIds.includes(p.id) && (
                          <Star className="h-3 w-3 shrink-0 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </Field>
            </Group>

            <Group
              title="Theme & background"
              icon={<Palette className="h-4 w-4" />}
              helper="Controls the page background behind product cards."
            >
              <Field label="Background style">
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { v: "solid", label: "Solid" },
                    { v: "gradient", label: "Gradient" },
                    { v: "image", label: "Hero only" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setBgStyle(o.v)}
                      className={cn(
                        "rounded-xl border py-1.5 text-xs font-medium transition-all",
                        bgStyle === o.v
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border hover:border-primary/40",
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </Field>
              {bgStyle === "solid" && (
                <Field label="Background colour" hint="Hex or CSS color (e.g. #f8fafc).">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bgColor || "#ffffff"}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="h-9 w-12 rounded-lg border border-border"
                    />
                    <Input
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      placeholder="#ffffff"
                      className="rounded-xl"
                    />
                  </div>
                </Field>
              )}
              {bgStyle === "gradient" && (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Enable gradient background</Label>
                    <Switch checked={gradEnabled} onCheckedChange={setGradEnabled} />
                  </div>
                  {gradEnabled && (
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="From">
                        <input
                          type="color"
                          value={gradFrom || "#ffffff"}
                          onChange={(e) => setGradFrom(e.target.value)}
                          className="h-9 w-full rounded-lg border border-border"
                        />
                      </Field>
                      <Field label="To">
                        <input
                          type="color"
                          value={gradTo || "#ffffff"}
                          onChange={(e) => setGradTo(e.target.value)}
                          className="h-9 w-full rounded-lg border border-border"
                        />
                      </Field>
                    </div>
                  )}
                </>
              )}
            </Group>

            <Group
              title="Product cards"
              icon={<LayoutGrid className="h-4 w-4" />}
              helper="How each product appears in the grid."
            >
              <Field label="Card density">
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { v: "compact", label: "Compact" },
                    { v: "standard", label: "Standard" },
                    { v: "large", label: "Large image" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setCardStyle(o.v)}
                      className={cn(
                        "rounded-xl border py-1.5 text-xs font-medium transition-all",
                        cardStyle === o.v
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border hover:border-primary/40",
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </Field>
            </Group>

            <Group
              title="Ribbons"
              icon={<Ribbon className="h-4 w-4" />}
              helper="Styling for the badge shown on product cards (e.g. New, Featured)."
            >
              <Field label="Ribbon colour" hint="Solid background colour.">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={ribbonColor || "#ffffff"}
                    onChange={(e) => setRibbonColor(e.target.value)}
                    className="h-9 w-12 rounded-lg border border-border"
                  />
                  <Input
                    value={ribbonColor}
                    onChange={(e) => setRibbonColor(e.target.value)}
                    placeholder="auto"
                    className="rounded-xl"
                  />
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Gradient from">
                  <input
                    type="color"
                    value={ribbonGradFrom || "#ffffff"}
                    onChange={(e) => setRibbonGradFrom(e.target.value)}
                    className="h-9 w-full rounded-lg border border-border"
                  />
                </Field>
                <Field label="Gradient to">
                  <input
                    type="color"
                    value={ribbonGradTo || "#ffffff"}
                    onChange={(e) => setRibbonGradTo(e.target.value)}
                    className="h-9 w-full rounded-lg border border-border"
                  />
                </Field>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Leave gradient blank to use solid colour, or both blank for the default theme
                ribbon.
              </p>
            </Group>

            <Group
              title="FlightDeck AI"
              icon={<Sparkles className="h-4 w-4" />}
              helper="Enables the AI assistant in the shop."
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-xs font-medium">Enable AI assistant</Label>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Shows the &quot;Not sure what to order?&quot; CTA on the hero, AI
                    recommendations, and guided buying prompts.
                  </p>
                </div>
                <Switch checked={aiEnabled} onCheckedChange={setAiEnabled} />
              </div>
            </Group>

            <Group
              title="Tags & metadata"
              icon={<Tag className="h-4 w-4" />}
              helper="Future SEO and merchandising controls will appear here."
            >
              <p className="text-[11px] text-muted-foreground">More controls coming soon.</p>
            </Group>

            <div className="pt-2">
              <Button className="w-full rounded-full gap-2" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4" />
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
